import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { catchError, map, tap } from 'rxjs/operators';

import moment from 'moment';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import * as z from 'zod';

import { Agreement, Product } from '../schemas';
import { Lambda } from './lambda';

const UserTime = z.strictObject({
  chargeable_time: z.number(),
  total_time: z.number(),
  sales_time: z.number(),
});

type UserTime = z.infer<typeof UserTime>;

const ProjectsTime = z.strictObject({
  success: z.literal(true),
  report: z.record(z.string(), z.number()),
  poReport: z.record(z.string(), z.number()),
});

export type ProjectsTime = z.infer<typeof ProjectsTime>;

const ProjectSp = z.record(z.literal(['Done']), z.number());

type ProjectSp = z.infer<typeof ProjectSp>;

const TimeRecord = z.strictObject({
  long_label: z.string().nullish(), // check 087 agreement that has this issue in May 2021
  overused: z.number(),
  profited: z.number(),
  profited_initial: z.number(),
  purchased: z.number(),
  remaining: z.number(),
  spent: z.number(),
  uncharged: z.number(),
  uncharged_initial: z.number(),
  unforeseen: z.number(),
  unused: z.number(),
  agreement_support: z.number(),
});

type TimeRecord = z.infer<typeof TimeRecord>;

const TimeDistribution = z.record(z.string(), TimeRecord).transform((record) =>
  Object.keys(record)
    .sort(sortDates)
    .map((key) => [key, record[key]] as [string, TimeRecord]),
);

export type TimeDistribution = z.infer<typeof TimeDistribution>;

const TimeTable = z.strictObject({
  lastUpdated: z.string(),
  time: TimeDistribution,
});

export type TimeTable = z.infer<typeof TimeTable>;

function sortDates(left: string, right: string) {
  const l = moment(left, 'MMMM Y', true);
  const r = moment(right, 'MMMM Y', true);
  if (l.isBefore(r)) {
    return -1;
  }
  if (r.isBefore(l)) {
    return 1;
  }
  return 0;
}

@Injectable({ providedIn: 'root' })
export class Jira {
  private http = inject(HttpClient);

  private url = inject(Lambda).getJiraUrl();
  private csvUrl = inject(Lambda).getJiraCsvUrl();

  link(code: string) {
    return `https://linkdigital.jira.com/projects/${code}`;
  }

  tables = signal<Record<string, TimeTable>>({});

  private outdated = new Set<string>();

  getUserTime(start: moment.Moment, end: moment.Moment, user: string) {
    return this.http
      .get<UserTime>([this.url, 'users', start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'), user].join('/'), {})
      .pipe(
        map((data) => UserTime.parse(data)),
        catchError((err) => {
          console.warn('Cannot fetch time of user %s: %o', user, err);
          return of({ total_time: 0, chargeable_time: 0, sales_time: 0 } as UserTime);
        }),
      );
  }

  getPoTime(start: string, end: string, projects: string[], jiraId?: string) {
    const url = [this.url, 'projects', start, end].join('/');
    const params = projects.reduce(
      (params: HttpParams, proj: string) => params.append('project', proj),
      new HttpParams(),
    );

    return this.http
      .get<ProjectsTime>(url, {
        params: params.append('jiraId', jiraId || ''),
      })
      .pipe(map((data) => ProjectsTime.parse(data)));
  }

  getProjectSpCompleted(project?: string) {
    return this.http
      .get<ProjectSp>(`${this.url}/story-points/${project}/Done`)
      .pipe(map((data) => ProjectSp.parse(data)));
  }

  private dropTableLog(id: string) {
    this.tables.update((data) => {
      const newData = { ...data };
      delete newData[id];
      return newData;
    });
    this.outdated.add(id);
  }

  private getTimeTable(id: string, dates: string[], payload: object = {}) {
    const refresh = this.outdated.delete(id);
    const cache = this.tables();
    if (!refresh && id in cache) return from([cache[id]]);
    const [start, end] = dates;

    let params = new HttpParams();
    if (start) {
      params = params.set('start_date', start);
    }
    if (end) {
      params = params.set('end_date', end);
    }
    if (refresh) {
      params = params.set('force', 'true');
    }
    const request = this.http
      .post<TimeTable>([this.url, 'detailed', id].join('/'), payload, {
        params: params,
      })
      .pipe(
        map((data) => TimeTable.parse(data)),
        tap((record) => this.tables.update((data) => ({ ...data, [id]: record }))),
      );
    return request;
  }

  private ejectStartDate(entity: Agreement | Product): string {
    if (!(entity as Agreement).AgreementNumber) {
      // For products, find the earliest date from all hour types and milestones
      let date = moment('2012-01-01');

      const allHours = [...entity.PurchasedHours, ...entity.UnchargeableHours]
        .map((item) => moment(`${item.Year} ${item.Month}`, 'YYYY MMMM'))
        .concat(((entity as Product).Milestones || []).map((m) => moment(m.CreatedAt)));

      for (const hoursDate of allHours) {
        if (date.isAfter(hoursDate)) {
          date = hoursDate;
        }
      }
      return String(`${date.year()}-${('0' + (date.month() + 1)).slice(-2)}-01`);
    } else {
      // For agreements, use the agreement's start date
      return (entity as Agreement).StartDate;
    }
  }

  getProjectTimelogs(project: Agreement | Product, force = false) {
    const endDate = moment().format('YYYY-MM-DD');
    const dateRange = [this.ejectStartDate(project), endDate];

    const milestoneHours = ((project as Product).Milestones || []).map((ms) => {
      const date = moment(ms.CreatedAt);
      const hourlyRate = ms.HourlyRate;
      const value = ms.Value;

      return {
        Month: date.format('MMMM'),
        Year: date.format('YYYY'),
        Hours: ~~(value / hourlyRate), // Integer division
      };
    });

    const payload = {
      purchased: [...project.PurchasedHours, ...milestoneHours],
      uncharged: project.UnchargeableHours || [],
      unforeseen: (project as Agreement).UnforeseenHours || [],
      uncharged_initial: project.UnchargeableHours || [],
      profited: project.ProfitableHours || [],
      profited_initial: project.ProfitableHours || [],
      support: (project as Agreement).SupportHours || 0,
      short_date: !(project as Agreement).AgreementNumber,
      extended: (project as Agreement).ExtendedLifetime || false,
    };

    if (force) {
      this.dropTableLog(project.JiraTitle);
    }

    return firstValueFrom(this.getTimeTable(project.JiraTitle, dateRange, payload));
  }

  /* generic download mechanism */
  downloadCsv(id: string, data?: object): Observable<ArrayBuffer> {
    let params = new HttpParams();

    if (data) {
      for (const key in data) {
        params = params.set(key, data[key]);
      }
    }
    const url = [this.csvUrl, id].join('/');

    // get the file
    try {
      return this.http.get(url, {
        params: params,
        responseType: 'arraybuffer',
      });
    } catch (err) {
      console.error(err);
      return of(new ArrayBuffer(0));
    }
  }

  /**
   * Returns the date of the last Tuesday.
   *
   * If today is Tuesday, it returns today's date. This is used as a checkpoint
   * for time tracking, ensuring that time logs are updated at least once a week.
   */
  lastCheckpoint() {
    const thu = moment().day(2);
    if (thu.isAfter(moment(), 'day')) {
      thu.subtract(7, 'days');
    }
    return thu;
  }
}
